import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import CodeIcon from '@mui/icons-material/Code';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CountdownTimer from '../components/CountdownTimer';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import FAQ from '../components/FAQ';
import PrizesSection from '../components/PrizesSection';
import ScheduleTimeline from '../components/Timeline';
// import JudgesMentors from '../components/JudgesMentors';

function Home() {
  return (
    <Container maxWidth="lg">
      {/* Hero Section */}
      <AnimatedSection animation="fadeIn">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{
            position: 'relative',
            textAlign: 'center',
            my: { xs: 4, sm: 6, md: 8 },
            py: { xs: 6, sm: 8, md: 12 },
            px: { xs: 2, sm: 3, md: 4 },
            background: 'linear-gradient(135deg, rgba(0, 100, 0, 0.95) 0%, rgba(46, 125, 50, 0.95) 50%, rgba(255, 215, 0, 0.95) 100%)',
            overflow: 'hidden',
            color: 'white',
            borderRadius: 4,
            mx: { xs: 1, sm: 2 },
            // Remove background image from ::before
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 50%, rgba(255, 215, 0, 0.2) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(0, 100, 0, 0.2) 0%, transparent 50%),
                url('/home_hackathon.jpg')
              `,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.3,
              zIndex: 1
            },
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1, transform: 'scale(1)' },
              '50%': { opacity: 0.9, transform: 'scale(1.02)' }
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {/* Logo above heading */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box
                    component="img"
                    src="/logo.png"
                    alt="Lagos Impact Hackathon logo"
                    sx={{
                      height: { xs: 115, sm: 154, md: 192 }, // 60% increase from original
                      width: 'auto',
                      mb: { xs: 1.5, sm: 2.5 },
                      filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.45))'
                    }}
                  />
                </Box>

                {/* Heading text */}
                <Typography
                  variant="h1"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem', lg: '4.5rem' },
                    fontWeight: 'bold',
                    textShadow: '3px 3px 6px rgba(0,0,0,0.5)',
                    mb: { xs: 2, sm: 3 },
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    textAlign: 'center'
                  }}
                >
                  Lagos Impact Hackathon 2025
                </Typography>
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: 'gold',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    mb: { xs: 1, sm: 2 },
                    textAlign: 'center',
                    fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem', lg: '1.7rem' },
                    lineHeight: 1.2,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                  }}
                >
                  Theme: Co-Creating Tech-Driven Solutions for Sustainable Urban Impact
                </Typography>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 500,
                    color: 'white',
                    opacity: 0.85,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                    mb: { xs: 2, sm: 3 },
                    textAlign: 'center',
                    fontStyle: 'italic'
                  }}
                >
                  Tech Innovation for Sustainable Impact
                </Typography>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Typography
                  variant="h5"
                  paragraph
                  sx={{
                    mb: { xs: 3, sm: 4 },
                    opacity: 0.95,
                    fontWeight: 500,
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                    px: { xs: 1, sm: 2 },
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                  }}
                >
                  The Lagos Impact Hackathon (LIH) is a 48-hour venture-building hackathon that brings together technology leaders, Web3 innovators, and development partners to build practical, SDG-aligned solutions that can scale in the real world.
                </Typography>
              </motion.div>

              {/* Countdown Timer */}
              <CountdownTimer
                targetDate="2025-12-11T00:00:00"
                variant="hero"
                showIcon={true}
              />

              {/* Stats Section - new compact pill design */}
              <Box
                sx={{
                  mb: { xs: 4, sm: 6 },
                  maxWidth: 900,
                  mx: 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: { xs: 1.5, sm: 2.5 }
                }}
              >
                {[
                  { icon: PeopleIcon, number: '200+', label: 'Elite Talents' },
                  { icon: EmojiEventsIcon, number: '48hrs', label: 'Non-Stop Innovation' },
                  { icon: CodeIcon, number: '2', label: 'Impact Tracks' }
                ].map((stat, index) => (
                  <AnimatedSection animation="scale" delay={0.6 + index * 0.1} key={index}>
                    <Paper
                      elevation={8}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1.5, sm: 2 },
                        px: { xs: 1.75, sm: 2.25 },
                        py: { xs: 1.25, sm: 1.5 },
                        borderRadius: 999,
                        bgcolor: 'rgba(0,0,0,0.25)',
                        backdropFilter: 'blur(10px)',
                        color: 'white',
                        minWidth: { xs: 230, sm: 260 },
                        maxWidth: 320
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 40, sm: 48 },
                          height: { xs: 40, sm: 48 },
                          borderRadius: '50%',
                          bgcolor: 'rgba(255, 215, 0, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <stat.icon sx={{ fontSize: { xs: 22, sm: 26 }, color: '#FFD700' }} />
                      </Box>
                      <Box sx={{ lineHeight: 1 }}>
                        <Typography
                          component="div"
                          sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', sm: '1.6rem' }, letterSpacing: '-0.01em' }}
                        >
                          {stat.number}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.9, fontSize: { xs: '0.72rem', sm: '0.8rem' } }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </Paper>
                  </AnimatedSection>
                ))}
              </Box>

              <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to="/register"
                    sx={{
                      bgcolor: 'secondary.main',
                      color: 'white',
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      animation: 'pulse 2s ease-in-out infinite',
                      boxShadow: '0 0 20px rgba(255, 215, 0, 0.4)',
                      border: '2px solid white',
                      '&:hover': {
                        bgcolor: '#FFA000',
                        color: 'white',
                        border: '2px solid white',
                        boxShadow: '0 0 30px rgba(255, 215, 0, 0.6)'
                      }
                    }}
                  >
                    Register Now
                  </Button>
                </motion.div>
                
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="outlined"
                    size="large"
                    component={Link}
                    to="/about"
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        borderColor: '#FFD700',
                        bgcolor: 'rgba(255, 215, 0, 0.1)'
                      }
                    }}
                  >
                    Learn More
                  </Button>
                </motion.div>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </AnimatedSection>

      {/* LIH Overview Copy */}
      <AnimatedSection animation="slideUp" delay={0.15}>
        <Box sx={{ my: { xs: 4, md: 6 } }}>
          <Typography variant="h4" component="h2" gutterBottom>
            The Lagos Impact Hackathon (LIH)
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
            LIH brings together over 200 of Nigeria’s most exceptional talents, including developers, engineers, strategists, and creatives, under one roof to design and deploy scalable solutions that align with the United Nations' Sustainable Development Goals (SDGs).
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
            Across Nigeria and the African region, many promising solutions for social good struggle to achieve lasting, scalable impact. Brilliant technology often fails
            for lack of a viable adoption pathway, while high-impact NGOs struggle to scale due to limited access to modern, adaptable technology.
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
            The Lagos Impact Hackathon bridges this divide, uniting world-class talent, cutting-edge technology, and purpose-driven organizations to
            co-create solutions that not only inspire but also endure.
          </Typography>
        </Box>
      </AnimatedSection>

      {/* Featured Tracks */}
      <AnimatedSection animation="fadeIn" delay={0.2}>
        <Box sx={{ my: 8 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h3" component="h2" gutterBottom>
                Featured Tracks
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Choose your path to create lasting impact
              </Typography>
            </Box>
            <Button
              variant="outlined"
              component={Link}
              to="/tracks"
              sx={{
                borderColor: 'white',
                color: 'white',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: 'white',
                  borderColor: 'white',
                  boxShadow: '0 0 8px 2px rgba(255,255,255,0.15)'
                }
              }}
            >
              View All Tracks
            </Button>
          </Box>

          <Grid container spacing={3}>
            {/* Tech Track Card */}
            <Grid item xs={12} md={6}>
              <AnimatedSection animation="slideUp" delay={0.1}>
                <Card sx={{
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 12px 25px rgba(0,0,0,0.15)',
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease'
                  }
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    zIndex: 2
                  }}>
                    <Chip
                      icon={<CodeIcon />}
                      label="Hack-a-thon"
                      sx={{
                        bgcolor: '#FFD700',
                        color: '#000',
                        fontWeight: 'bold',
                        '& .MuiChip-icon': { color: '#000' }
                      }}
                    />
                  </Box>
                  <Box sx={{
                    height: 180,
                    background: `
                      linear-gradient(45deg, rgba(0, 100, 0, 0.8), rgba(46, 125, 50, 0.8)),
                      url('/home_hackathon.jpg')
                    `,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CodeIcon sx={{ fontSize: 50, color: 'white' }} />
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" component="h3" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      Tech for Impact Track
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Design and build functional, open-source technologies aligned with the United Nations' Sustainable Development Goals.
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {[
                        { icon: PeopleIcon, value: '100+', label: 'Participants' },
                        { icon: AccessTimeIcon, value: '48hrs', label: 'Duration' },
                        { icon: EmojiEventsIcon, value: '6', label: 'Challenges' }
                      ].map((stat, idx) => (
                        <Grid item xs={4} key={idx}>
                          <Box sx={{ textAlign: 'center' }}>
                            <stat.icon sx={{ color: 'primary.main', mb: 0.5 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {stat.value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {stat.label}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {['AI', 'Web3', 'Blockchain', 'Data Science'].map((tech, idx) => (
                        <Chip
                          key={idx}
                          size="small"
                          label={tech}
                          sx={(theme) => ({
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.15)' : '#E8F5E8',
                            color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#2E7D32'
                          })}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </Grid>

            {/* Marketing Track Card */}
            <Grid item xs={12} md={6}>
              <AnimatedSection animation="slideUp" delay={0.2}>
                <Card sx={{
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 12px 25px rgba(0,0,0,0.15)',
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease'
                  }
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    zIndex: 2
                  }}>
                    <Chip
                      icon={<CampaignIcon />}
                      label="Mark-a-thon"
                      sx={{
                        bgcolor: '#FFF3E0',
                        color: '#E65100',
                        fontWeight: 'bold',
                        '& .MuiChip-icon': { color: '#E65100' }
                      }}
                    />
                  </Box>

                  <Box sx={{
                    height: 180,
                    background: `
                      linear-gradient(45deg, rgba(255, 215, 0, 0.8), rgba(245, 166, 35, 0.8)),
                      url('/markathon.jpg')
                    `,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.3s ease'
                  }}>
                    <CampaignIcon sx={{ fontSize: 50, color: 'white' }} />
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" component="h3" gutterBottom sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                      Marketing for Impact Track
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Design launch-ready communication and visibility campaigns for SDG-aligned NGOs.
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {[
                        { icon: PeopleIcon, value: '50+', label: 'Participants' },
                        { icon: AccessTimeIcon, value: '48hrs', label: 'Duration' },
                        { icon: CampaignIcon, value: '5', label: 'NGOs' }
                      ].map((stat, idx) => (
                        <Grid item xs={4} key={idx}>
                          <Box sx={{ textAlign: 'center' }}>
                            <stat.icon sx={{ color: 'secondary.main', mb: 0.5 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                              {stat.value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {stat.label}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {['Strategy', 'Branding', 'Communications'].map((skill, idx) => (
                        <Chip
                          key={idx}
                          size="small"
                          label={skill}
                          sx={(theme) => ({
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,215,0,0.15)' : '#FFF8E1',
                            color: theme.palette.mode === 'dark' ? theme.palette.secondary.dark : '#F57C00'
                          })}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </Grid>
          </Grid>
        </Box>
      </AnimatedSection>

      {/* Prizes Section */}
      <PrizesSection />

      {/* Schedule Timeline */}
      <ScheduleTimeline />

      {/* Judges & Mentors */}
      {/* <JudgesMentors /> */}

      {/* FAQ Section */}
      <FAQ />

      {/* Call to Action */}
      <AnimatedSection animation="scale">
        <Box sx={{
          textAlign: 'center',
          my: 8,
          py: 6,
          background: 'linear-gradient(135deg, #006400 0%, #2E7D32 50%, #FFD700 100%)',
          color: 'white',
          borderRadius: 3
        }}>
          <motion.div
            animate={{
              scale: [1, 1.02, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Ready to Make an Impact?
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 4, opacity: 0.9 }}>
              Join Nigeria's elite innovators in solving the biggest challenges facing our communities.
            </Typography>
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              style={{ display: 'inline-block' }}
            >
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/tracks"
                sx={{
                  bgcolor: 'transparent',
                  color: 'white',
                  fontWeight: 'bold',
                  px: 4,
                  py: 1.5,
                  border: '2px solid white',
                  boxShadow: '0 0 0 rgba(0,0,0,0)',
                  transition: 'all .25s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.12)',
                    color: 'white',
                    boxShadow: '0 0 12px 2px rgba(255,255,255,0.35)',
                    border: '2px solid white'
                  }
                }}
              >
                Explore Tracks
              </Button>
            </motion.div>
          </motion.div>
        </Box>
      </AnimatedSection>
    </Container>
  );
}

export default Home;
